import React, { useState, useEffect } from 'react';
import { HistoryItem } from '@/types/history';
import {
  getAllHistoryItems,
  deleteHistoryItem,
  clearAllHistory,
  saveHistoryItem
} from '@/lib/storage/db';
import { Button } from '@/components/ui/Button';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { useToast } from '@/components/ui/Toast';
import { QRRenderer } from '@/components/qr/QRRenderer';
import { downloadQRCode } from '@/lib/qr/export';
import {
  History,
  Star,
  Search,
  Trash2,
  Download,
  ShieldCheck,
  Globe,
  Wifi,
  Contact,
  CreditCard,
  Type
} from 'lucide-react';

interface HistoryManagerProps {
  onLoadItem: (item: HistoryItem) => void;
}

export const HistoryManager: React.FC<HistoryManagerProps> = ({ onLoadItem }) => {
  const { showToast } = useToast();
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [isClearAllOpen, setIsClearAllOpen] = useState(false);

  const loadHistory = async () => {
    try {
      setIsLoading(true);
      const list = await getAllHistoryItems();
      setItems(list);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const handleRequestDelete = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setPendingDeleteId(id);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    await deleteHistoryItem(pendingDeleteId);
    setItems((prev) => prev.filter((i) => i.id !== pendingDeleteId));
    showToast('info', 'Item Removed from History');
  };

  const handleToggleFavorite = async (item: HistoryItem, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = { ...item, isFavorite: !item.isFavorite, updatedAt: Date.now() };
    await saveHistoryItem(updated);
    setItems((prev) => prev.map((i) => (i.id === item.id ? updated : i)));
    showToast('success', updated.isFavorite ? 'Saved to Favorites' : 'Removed from Favorites');
  };

  const handleDownload = async (item: HistoryItem, e: React.MouseEvent) => {
    e.stopPropagation();
    await downloadQRCode(item.rawText, item.design, {
      format: 'png',
      size: 1024,
      filename: item.title.toLowerCase().replace(/\s+/g, '-')
    });
    showToast('success', 'Download Started');
  };

  const handleClearAll = async () => {
    await clearAllHistory();
    setItems([]);
    showToast('info', 'Local history cleared');
  };

  const filtered = items.filter((item) => {
    const matchesSearch =
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.rawText.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.type.toLowerCase().includes(searchQuery.toLowerCase());

    if (!matchesSearch) return false;
    if (filterType === 'favorites') return item.isFavorite;
    if (filterType !== 'all') return item.type === filterType;
    return true;
  });

  const typeIcons: Record<string, React.ReactNode> = {
    url: <Globe className="h-4 w-4 text-primary" />,
    wifi: <Wifi className="h-4 w-4 text-primary" />,
    vcard: <Contact className="h-4 w-4 text-primary" />,
    upi: <CreditCard className="h-4 w-4 text-primary" />,
    text: <Type className="h-4 w-4 text-muted-foreground" />
  };

  return (
    <div className="w-full space-y-6">
      {/* Search and Privacy Bar */}
      <div className="p-6 rounded-2xl bg-card border border-border space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h3 className="text-[21px] font-semibold text-foreground tracking-tight flex items-center gap-2">
              <History className="h-5 w-5 text-warning" />
              History & Favorites
            </h3>
            <p className="text-[14px] text-success mt-0.5 flex items-center gap-1.5 font-normal">
              <ShieldCheck className="h-3.5 w-3.5" />
              Stored 100% locally in browser IndexedDB &bull; Zero external servers
            </p>
          </div>

          {items.length > 0 && (
            <button
              onClick={() => setIsClearAllOpen(true)}
              className="text-[12px] text-destructive hover:underline cursor-pointer"
            >
              Clear All History
            </button>
          )}
        </div>

        {/* Filters and Search Input */}
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
          <div className="flex items-center gap-1.5 p-1 bg-muted rounded-full overflow-x-auto">
            {[
              { id: 'all', label: 'All' },
              { id: 'favorites', label: '★ Favorites' },
              { id: 'url', label: 'URLs' },
              { id: 'wifi', label: 'Wi-Fi' },
              { id: 'vcard', label: 'Contacts' },
              { id: 'upi', label: 'UPI' }
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setFilterType(f.id)}
                className={`px-3.5 py-1.5 rounded-full text-[12px] font-normal tracking-tight transition-all whitespace-nowrap cursor-pointer ${
                  filterType === f.id
                    ? 'bg-card text-primary font-medium shadow-xs'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="relative flex-1 max-w-xs">
            <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search history..."
              className="w-full pl-9 pr-3.5 py-1.5 text-[14px] rounded-full bg-muted border border-border text-foreground focus:outline-none focus:border-ring tracking-tight"
            />
          </div>
        </div>
      </div>

      {/* Grid of Saved History Items (Store Utility Card style at rounded.lg = 18px) */}
      {isLoading ? (
        <div className="py-16 text-center text-muted-foreground">Loading saved items...</div>
      ) : filtered.length === 0 ? (
        <div className="p-12 text-center text-muted-foreground bg-card rounded-2xl border border-border space-y-3">
          <History className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="text-[17px] font-medium text-foreground">No QR items in history</p>
          <p className="text-[14px] max-w-sm mx-auto">
            Codes you generate or favorite in QR Studio will automatically show up here.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((item) => (
            <div
              key={item.id}
              onClick={() => onLoadItem(item)}
              className="group p-5 rounded-2xl bg-card border border-border hover:border-muted-foreground transition-all duration-150 cursor-pointer flex flex-col justify-between"
            >
              <div>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-muted">
                      {typeIcons[item.type] || <Type className="h-4 w-4" />}
                    </div>
                    <div>
                      <h4 className="text-[14px] font-semibold text-foreground truncate max-w-[140px] tracking-tight">
                        {item.title}
                      </h4>
                      <span className="text-[11px] text-muted-foreground">
                        {new Date(item.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={(e) => handleToggleFavorite(item, e)}
                    className={`p-1.5 rounded-full border transition-all cursor-pointer ${
                      item.isFavorite
                        ? 'bg-warning text-warning-foreground border-warning'
                        : 'text-muted-foreground hover:text-warning border-transparent'
                    }`}
                  >
                    <Star className="h-3.5 w-3.5 fill-current" />
                  </button>
                </div>

                {/* Thumbnail QR Display Resting on Surface with Apple Product Shadow */}
                <div className="p-4 rounded-xl bg-muted border border-border flex items-center justify-center mb-3">
                  <div className="p-2 rounded-lg bg-white product-shadow">
                    <QRRenderer data={item.rawText} design={item.design} size={130} />
                  </div>
                </div>

                <p className="font-mono text-[12px] text-muted-foreground truncate mb-3 bg-muted p-2 rounded-lg">
                  {item.rawText}
                </p>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-border text-[14px]">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => handleDownload(item, e)}
                  leftIcon={<Download className="h-3.5 w-3.5" />}
                  className="px-3 text-primary"
                >
                  PNG
                </Button>

                <div className="flex items-center gap-1.5">
                  <button
                    onClick={(e) => handleRequestDelete(item.id, e)}
                    className="p-1.5 text-muted-foreground hover:text-destructive rounded-full transition-colors cursor-pointer"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => onLoadItem(item)}
                    className="px-4"
                  >
                    Edit
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        isOpen={pendingDeleteId !== null}
        onClose={() => setPendingDeleteId(null)}
        onConfirm={handleConfirmDelete}
        title="Delete this item?"
        description="This QR code will be permanently removed from your local history."
        confirmLabel="Delete"
      />

      <ConfirmModal
        isOpen={isClearAllOpen}
        onClose={() => setIsClearAllOpen(false)}
        onConfirm={handleClearAll}
        title="Clear all history?"
        description="This permanently clears your local QR history. This cannot be undone."
        confirmLabel="Clear All"
      />
    </div>
  );
};
